/**
 * Capability Resolver — decision contract (CR0 keystone)
 *
 * @see doc/system-completion-plan/01-target/CAPABILITY_RESOLVER_SSOT.md §3
 */

/** Adapter / composite source that produced the decision */
export type CapabilitySource =
  | 'entity'
  | 'plane'
  | 'version'
  | 'domain'
  | 'field'
  | 'object_state'
  | 'assignment'
  | 'space'
  | 'sensitive'
  | 'physical_access'
  | 'composite';

/** Operator-facing UI hint when action is denied */
export type CapabilitySuggestedAction =
  | 'request_access'
  | 'step_up_mfa'
  | 'contact_owner'
  | 'complete_assignment'
  | 'switch_space'
  | 'restore_object';

/** Machine-readable deny reason (reason field may carry localized label) */
export enum CapabilityDenyReason {
  EntityInactive = 'entity_inactive',
  PlaneForbidden = 'plane_forbidden',
  PermissionVersionStale = 'permission_version_stale',
  DomainDenied = 'domain_denied',
  FieldDenied = 'field_denied',
  ObjectStateForbidden = 'object_state_forbidden',
  AssignmentMissing = 'assignment_missing',
  SpaceForbidden = 'space_forbidden',
  SensitiveRevalidationRequired = 'sensitive_revalidation_required',
  PermissionMissing = 'permission_missing',
}

export interface CapabilityDecision {
  /** Final allow/deny */
  allowed: boolean;
  /** Operator-facing deny message (Korean labels allowed) */
  reason?: string;
  /** Missing or required permission keys on deny — always an array (CR-7) */
  requiredPermissions: string[];
  /** Adapter that produced this decision */
  source: CapabilitySource;
  /** Sensitive action — fresh DB revalidation required before grant */
  sensitive: boolean;
  /** UI hint for denied actions */
  suggestedAction?: CapabilitySuggestedAction;
  /** Structured deny code when denied */
  denyReason?: CapabilityDenyReason;
}

export function allowDecision(source: CapabilitySource = 'composite'): CapabilityDecision {
  return {
    allowed: true,
    requiredPermissions: [],
    source,
    sensitive: false,
  };
}

export function denyDecision(
  input: Pick<CapabilityDecision, 'source'> &
    Partial<Omit<CapabilityDecision, 'allowed' | 'source'>> & { allowed?: false }
): CapabilityDecision {
  return {
    allowed: false,
    requiredPermissions: input.requiredPermissions ?? [],
    source: input.source,
    sensitive: input.sensitive ?? false,
    ...(input.reason !== undefined ? { reason: input.reason } : {}),
    ...(input.suggestedAction !== undefined ? { suggestedAction: input.suggestedAction } : {}),
    ...(input.denyReason !== undefined ? { denyReason: input.denyReason } : {}),
  };
}
